import { memo, useCallback, useEffect, useMemo, useRef } from 'react';
import {
  FlatList,
  LayoutChangeEvent,
  LayoutRectangle,
  ListRenderItemInfo,
  StyleSheet,
  Text,
  View,
} from 'react-native';
import { MainStackProps } from '../../../../types/screens';
import { useOnTop } from '../../../../common/hooks/useOnTop';
import { FocusStateType, useFocusState } from '../../../../common/hooks';
import useGlobalStyles from '../../../../common/style_context';
import useScale from '../../../../common/hooks/useScale';
import Constants from '../../../../utils/constants';
import { computed, reaction } from 'mobx';
import KeyWrapper from '../../../../components/key_wrapper';
import TvPressable from '../../../../components/tv_pressable';
import { commonStyles } from '../../../../common/styles';
import localization from '../../../../common/localization';
import { observer } from 'mobx-react-lite';
import AppColors from '../../../../common/colors';
import { IdNameType, VoidCb } from '../../../../types/others';

type Props = MainStackProps<'SearchFilterDialog'>;

const dialogStyles = [
  'w=30%,h=100%,bg=#000000BF,pl=24',
  'fs=48,fw=700,c=white,mb=40',
];

const SearchFilterDialog = memo<Props>(({ navigation, route: { params } }) => {
  const enabled = useOnTop(navigation);

  const items = params.filters;
  const focusState = useFocusState(items.length + 1);

  const [container, titleStyle] = useGlobalStyles(dialogStyles);
  const scales = useScale();
  const style = useMemo(
    () =>
      StyleSheet.compose(container, {
        paddingTop: Constants.tvSafeArea.top / scales[0],
        paddingBottom: Constants.tvSafeArea.bottom / scales[0],
        paddingRight: Constants.tvSafeArea.horizontal / scales[0],
      }),
    [container, scales],
  );
  const itemSize = useMemo(
    () => ((itemHeight + itemGap) / scales[0]) * scales[1],
    [scales],
  );

  const ref = useRef<FlatList>(null);
  const prev = useRef(0);
  const layout = useRef<LayoutRectangle>(undefined);

  useEffect(() => {
    return reaction(
      () => focusState.focused,
      value => {
        if (value === undefined) return;

        if (value < prev.current) {
          ref.current?.scrollToIndex({ animated: false, index: value });
          prev.current = value;
          return;
        }
        if (layout.current === undefined) return;
        const count = Math.floor(layout.current.height / itemSize);
        if (value >= prev.current + count) {
          const index = value - count + 1;
          ref.current?.scrollToIndex({ animated: false, index });
          prev.current = index;
        }
      },
    );
  }, [itemSize]);

  const up = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.decrement();
  }, []);
  const down = useCallback((keyUp?: boolean) => {
    if (keyUp) return;
    focusState.increment();
  }, []);
  const right = useCallback(
    (keyUp?: boolean) => {
      if (keyUp) return;
      focusState.focused = items.length;
    },
    [items.length],
  );
  const confirm = useCallback(() => {
    const f = focusState._focused;
    if (f === items.length) {
      navigation.pop();
      return;
    }

    const item = items[f];
    if (item.id > 0) {
      navigation.replace('SearchMoviesResult', {
        tab: 3,
        category: items[f].id,
        query: params.query,
        title: items[f].name,
      });
    } else if (item.id === 0) {
      navigation.replace('SearchChannelsResult', {
        query: params.query,
      });
    } else {
      navigation.replace('SearchEpgResult', {
        query: params.query,
      });
    }
  }, [items]);
  const cancel = useCallback(() => navigation.pop(), []);

  const renderItem = useCallback(
    ({ item, index }: ListRenderItemInfo<IdNameType>) => (
      <FilterItem
        title={item.name}
        index={index}
        focusState={focusState}
        onPress={confirm}
      />
    ),
    [confirm],
  );
  const getItemLayout = useCallback(
    (_: any, index: number) => ({
      length: itemSize,
      offset: itemSize * index,
      index,
    }),
    [itemSize],
  );
  const keyExtractor = useCallback((item: IdNameType) => `${item.id}`, []);

  const onLayout = useCallback((e: LayoutChangeEvent) => {
    const l = e.nativeEvent.layout;
    if (l.height === 0) return;
    layout.current = l;
  }, []);

  return (
    <KeyWrapper
      enabled={enabled}
      up={up}
      down={down}
      right={right}
      confirm={confirm}
      cancel={cancel}
      style={styles.container}>
      <TvPressable onPress={cancel} style={commonStyles.fill} />
      <View style={style}>
        <Text style={titleStyle}>{localization.content.searchFilter}</Text>
        <View onLayout={onLayout} style={commonStyles.f1}>
          <FlatList
            data={items}
            renderItem={renderItem}
            keyExtractor={keyExtractor}
            getItemLayout={getItemLayout}
            removeClippedSubviews={false}
            scrollEnabled={false}
            showsVerticalScrollIndicator={false}
            ref={ref}
          />
        </View>
        <CloseButton index={items.length} focusState={focusState} />
      </View>
    </KeyWrapper>
  );
});
SearchFilterDialog.displayName = 'SearchFilterDialogTv';

const itemHeight = 65;
const itemGap = 6;

const itemStyles = [
  `fr,ac,h=${itemHeight},mb=${itemGap},pl=24`,
  `fill,bg=${AppColors.mainBlue},br=12`,
  `hide,fill,bg=${AppColors.mainBlue},br=12`,
  'f=1,fs=32,fw=500,c=white,ml=16',
];

type ItemProps = {
  title: string;
  index: number;
  focusState: FocusStateType;
  onPress?: VoidCb;
};

const FilterItem = observer<ItemProps>(
  ({ title, index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();
    const [container, focusedContainer, unfocusedContainer, textStyle] =
      useGlobalStyles(itemStyles);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, [index, onPress]);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : unfocusedContainer} />
        <Text style={textStyle}>{title}</Text>
      </TvPressable>
    );
  },
);

const resetStyles = [
  `ac,bg=#393939,pv=16,br=16`,
  `fill,bg=${AppColors.mainBlue},br=16`,
  'fs=28,fw=500,c=white',
];

const CloseButton = observer<Omit<ItemProps, 'title'>>(
  ({ index, focusState, onPress }) => {
    const focused = computed(() => focusState.focused === index).get();

    const [container, focusedContainer, textStyle] =
      useGlobalStyles(resetStyles);

    const press = useCallback(() => {
      focusState.focused = index;
      onPress?.();
    }, [index, onPress]);

    return (
      <TvPressable onPress={press} style={container}>
        <View style={focused ? focusedContainer : commonStyles.fill} />
        <Text style={textStyle}>{localization.general.cancel}</Text>
      </TvPressable>
    );
  },
);

const styles = StyleSheet.create({
  container: {
    flex: 1,
    alignItems: 'flex-end',
  },
});

export default SearchFilterDialog;
